import { Link, useParams } from "react-router";
import { ArrowRight, ChevronLeft, Clock3, Layers3, Sparkles, TriangleAlert } from "lucide-react";

import { StatusPill } from "@/components/common/status-pill";
import { formatShortDate } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";
import { Badge } from "@runcheetah/ui";
import { Button } from "@runcheetah/ui";
import { Card, CardContent } from "@runcheetah/ui";
import { Skeleton } from "@runcheetah/ui";

export const CAMPAIGN_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  draft: { label: "Draft", className: "border-slate-200 bg-slate-50 text-slate-700" },
  pending_review: {
    label: "Pending Review",
    className: "border-yellow-200 bg-yellow-50 text-yellow-700",
  },
  approved: { label: "Approved", className: "border-blue-200 bg-blue-50 text-blue-700" },
  active: { label: "Active", className: "border-green-200 bg-green-50 text-green-700" },
  paused: { label: "Paused", className: "border-yellow-200 bg-yellow-50 text-yellow-700" },
  completed: { label: "Completed", className: "border-blue-200 bg-blue-50 text-blue-700" },
};

export function CampaignsPage() {
  const { id: brandId } = useParams<{ id: string }>();

  const brandQuery = trpc.admin.brands.get.useQuery({ id: brandId ?? "" }, { enabled: !!brandId });
  const campaignsQuery = trpc.admin.campaigns.list.useQuery(
    { brandId: brandId ?? "" },
    { enabled: !!brandId },
  );

  if (!brandId) {
    return null;
  }

  const brand = brandQuery.data;
  const campaigns = campaignsQuery.data;

  return (
    <div className="space-y-8 px-6 py-6 lg:px-8 xl:px-10">
      <header className="space-y-5 border-b border-border/60 pb-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="size-8" asChild>
            <Link to={`/brands/${brandId}`}>
              <ChevronLeft className="size-4" />
            </Link>
          </Button>
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Campaigns</h2>
            <p className="text-sm text-muted-foreground">{brand?.sourceDomain ?? brandId}</p>
          </div>
        </div>

        {!campaignsQuery.isPending && campaigns ? (
          <div className="flex flex-wrap items-center gap-2.5 text-sm text-muted-foreground">
            <Badge variant="outline" className="rounded-full bg-card px-3 py-1.5 text-[11px]">
              {campaigns.length} campaigns
            </Badge>
            <Badge variant="outline" className="rounded-full bg-card px-3 py-1.5 text-[11px]">
              {campaigns.reduce((sum, campaign) => sum + campaign.review.totalSlots, 0)} planned
              posts
            </Badge>
            <Badge variant="outline" className="rounded-full bg-card px-3 py-1.5 text-[11px]">
              {campaigns.reduce((sum, campaign) => sum + campaign.review.readySlots, 0)} ready
            </Badge>
            <Badge variant="outline" className="rounded-full bg-card px-3 py-1.5 text-[11px]">
              {campaigns.reduce((sum, campaign) => sum + campaign.review.inFlightSlots, 0)} in
              progress
            </Badge>
          </div>
        ) : null}
      </header>

      {campaignsQuery.isPending ? (
        <div className="grid gap-5 xl:grid-cols-2">
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      ) : campaignsQuery.error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {campaignsQuery.error.message}
        </div>
      ) : !campaigns || campaigns.length === 0 ? (
        <div className="flex h-40 items-center justify-center rounded-lg border border-dashed">
          <p className="text-sm text-muted-foreground">No campaigns found for this brand.</p>
        </div>
      ) : (
        <div className="grid gap-5 xl:grid-cols-2">
          {campaigns.map((campaign) => {
            const statusConfig = CAMPAIGN_STATUS_CONFIG[campaign.status];
            const attentionLevel =
              campaign.status === "pending_review"
                ? "ready"
                : campaign.review.failedSlots > 0
                  ? "attention"
                  : campaign.review.inFlightSlots > 0
                    ? "progress"
                    : campaign.review.readySlots > 0
                      ? "ready"
                      : "idle";

            return (
              <Card
                key={campaign.id}
                className="overflow-hidden border-border/60 bg-card/95 shadow-sm"
              >
                <CardContent className="grid gap-5 p-5 sm:grid-cols-[minmax(0,1fr)_220px]">
                  <div className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <StatusPill
                        label={statusConfig?.label ?? campaign.status}
                        className={statusConfig?.className}
                      />
                      <span className="text-xs uppercase tracking-[0.14em] text-foreground/52">
                        {formatShortDate(campaign.startDate)} to {formatShortDate(campaign.endDate)}
                      </span>
                    </div>

                    <div className="space-y-2">
                      <Link
                        to={`/brands/${brandId}/campaigns/${campaign.id}`}
                        className="block text-xl font-semibold tracking-tight text-foreground hover:underline"
                      >
                        {campaign.name}
                      </Link>
                      <p className="max-w-2xl text-sm leading-7 text-foreground/68">
                        {campaign.brief.summary}
                      </p>
                    </div>

                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                      <MetricCard
                        label="Planned Posts"
                        value={String(campaign.review.totalSlots)}
                        icon={Layers3}
                      />
                      <MetricCard
                        label="Ready"
                        value={String(campaign.review.readySlots)}
                        icon={Sparkles}
                      />
                      <MetricCard
                        label="In Progress"
                        value={String(campaign.review.inFlightSlots)}
                        icon={Clock3}
                      />
                      <MetricCard
                        label="Needs Attention"
                        value={String(campaign.review.failedSlots)}
                        icon={TriangleAlert}
                        tone={campaign.review.failedSlots > 0 ? "danger" : "neutral"}
                      />
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-sm text-foreground/62">
                      <span>{campaign.generationTaskCount} attempts</span>
                      {campaign.review.nextScheduledFor ? (
                        <span>
                          Next planned post {formatShortDate(campaign.review.nextScheduledFor)}
                        </span>
                      ) : null}
                    </div>

                    <div className="flex items-center gap-3">
                      <AttentionBadge level={attentionLevel} />
                      <Button asChild size="sm" className="gap-2 rounded-xl">
                        <Link to={`/brands/${brandId}/campaigns/${campaign.id}`}>
                          Open Campaign Board
                          <ArrowRight className="size-4" />
                        </Link>
                      </Button>
                    </div>
                  </div>

                  <div className="flex min-h-[16rem] items-center justify-center rounded-[24px] bg-muted/15 p-4">
                    {campaign.review.featuredSlot?.previewAsset ? (
                      <div className="space-y-3">
                        <img
                          src={campaign.review.featuredSlot.previewAsset.signedUrl}
                          alt={`Featured creative from ${campaign.name}`}
                          width={campaign.review.featuredSlot.previewAsset.width ?? 1080}
                          height={campaign.review.featuredSlot.previewAsset.height ?? 1350}
                          className="mx-auto max-h-60 w-auto rounded-[20px] object-contain shadow-sm"
                          loading="lazy"
                        />
                        <div className="space-y-1 text-center">
                          <div className="line-clamp-2 text-sm font-medium text-foreground">
                            {campaign.review.featuredSlot.concept}
                          </div>
                          <div className="text-xs uppercase tracking-[0.12em] text-foreground/48">
                            {formatShortDate(campaign.review.featuredSlot.scheduledFor)}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2 text-center">
                        <div className="text-sm font-medium text-foreground">
                          {campaign.status === "pending_review"
                            ? "Preview pending review"
                            : "No featured creative yet"}
                        </div>
                        <p className="text-sm leading-6 text-foreground/60">
                          {campaign.status === "pending_review"
                            ? "The first two planned posts will appear here as preview once generation completes."
                            : "This campaign has not produced a completed creative preview yet."}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MetricCard({
  label,
  value,
  icon: Icon,
  tone = "neutral",
}: {
  label: string;
  value: string;
  icon: typeof Layers3;
  tone?: "neutral" | "danger";
}) {
  return (
    <div className="rounded-2xl border border-border/60 bg-background/80 px-4 py-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/48">
          {label}
        </div>
        <Icon className={tone === "danger" ? "size-4 text-red-500" : "size-4 text-foreground/45"} />
      </div>
      <div className="mt-3 text-xl font-semibold tracking-tight text-foreground">{value}</div>
    </div>
  );
}

function AttentionBadge({ level }: { level: "attention" | "progress" | "ready" | "idle" }) {
  if (level === "attention") {
    return (
      <Badge className="rounded-full bg-red-50 text-red-700 border border-red-200">
        Needs attention
      </Badge>
    );
  }

  if (level === "progress") {
    return (
      <Badge className="rounded-full bg-blue-50 text-blue-700 border border-blue-200">
        In progress
      </Badge>
    );
  }

  if (level === "ready") {
    return (
      <Badge className="rounded-full bg-green-50 text-green-700 border border-green-200">
        Ready for review
      </Badge>
    );
  }

  return (
    <Badge className="rounded-full bg-slate-50 text-slate-700 border border-slate-200">
      Planning
    </Badge>
  );
}
